import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { pathHomePage, WEB_SERVICE_MIMS, protocols, applications } from "../../config/constants";
import { isPhraseProjectHospital, isNestProjectHospital } from "../../config/projectFunctions";
import { isClinicOrHome, isAppOrPlus, isApp, isPlus } from "../../config/validateApplication";
import Axios from "../../config/axios";
import UseFetch from "../../config/useFetch";
// Components
import { Form } from "./styles";
import Loader from "../loader";
import PatientNavigation from "../patientNavigation";
import SoftwareMenu from "../softwareMenu";
import Title from "../title";
import PrescriptionsInfo from "./sections/prescriptionsInfo";
import ActionsManagement from "./sections/actionsManagement";
import DismissibleWarningMessage from "../dismissibleWarningMessage";
import ClinicAndHomeProtocols from "./sections/renderProtocols/clinicAndHome";
import AppProtocols from "./sections/renderProtocols/app";
import PlusProtocols from "./sections/renderProtocols/plus";
import RoutineInfo from "./sections/routineInfo";
import CoachStatus from "./sections/coachStatus";
import CheckChangesWithoutSaving from "./sections/checkChangesWithoutSaving";

const PatientPrescriptions = ({ software }) => {
  const [isDisabledInputs, setIsDisabledInputs] = useState(true);
  const [isColorToggleActive, setIsColorToggleActive] = useState(false);
  const [rechargeComponent, setRechargeComponent] = useState(false);
  const [hospitalProtocols, setHospitalProtocols] = useState([]);
  const [minutesUsage, setMinutesUsage] = useState({});
  const [protocolsSessionsNumber, setProtocolsSessionsNumber] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [platformFilter, setPlatformFilter] = useState("");
  const [isIncompatibleWithAR, setIsIncompatibleWithAR] = useState(false);
  const [prescriptionsOrder, setPrescriptionsOrder] = useState([]);
  let [prescriptionsToDatabase] = useState([]);
  // Loaders
  const [isLoadingHospitalProtocols, setIsLoadingHospitalProtocols] = useState(true);
  const [isLoadingPrescriptions, setIsLoadingPrescriptions] = useState(true);
  const [isLoadingSessionsNumber, setIsLoadingSessionsNumber] = useState(true);
  // Coach status
  const [showCoachStatus, setShowCoachStatus] = useState(false);
  const [isCoachStatus, setIsCoachStatus] = useState(false);
  // Protocol controls
  const [rechargeProtocolControls, setRechargeProtocolControls] = useState(false);
  const [protocolControls, setProtocolControls] = useState({});
  const [twisterBuddyProtocolControls, setTwisterBuddyProtocolControls] = useState({});
  const [twisterBuddyControlsErrors, setTwisterBuddyControlsErrors] = useState(false);
  const [costumePartyProtocolControls, setCostumePartyProtocolControls] = useState({});
  const [costumePartyControlErrors, setCostumePartyControlErrors] = useState(false);
  const [gentleGiantProtocolControls, setGentleGiantProtocolControls] = useState({});
  const [gentleGiantControlErrors, setGentleGiantControlErrors] = useState(false);

  const { clinicianToken, patientId } = useParams();
  const hospitalId = useSelector(({ clinician }) => clinician.data.HOSPITAL_ID);
  const hospitalName = useSelector(({ clinician }) => clinician.data.HOSPITAL_NAME);
  const [t] = useTranslation("global");

  // Fetching hospital protocols
  useEffect(() => {
    if (!software || !hospitalId) return;

    Axios("get", `${WEB_SERVICE_MIMS}/hospitals/protocols/${applications[software].id}/${hospitalId}`)
      .then(({ data }) => {
        const setProtocolPlatformsAndCategories = data.map((protocolPlatform) => {
          const PLATFORMS = protocolPlatform.platforms.map((platforms) => `${platforms.PLATFORM}_${platforms.SUBCATEGORY}`);
          return {
            PROTOCOL_ID: protocolPlatform.PROTOCOL_ID,
            PLATFORMS,
          };
        });
        setHospitalProtocols(setProtocolPlatformsAndCategories);
        setIsLoadingHospitalProtocols(false);
      })
      .catch((error) => {
        console.log("Get hospital protocols: ", error);
      });
  }, [software, hospitalId]);

  // Fetching prescriptions
  useEffect(() => {
    UseFetch("get", `${WEB_SERVICE_MIMS}/prescriptions/get/${patientId}/${software}`, new AbortController(), null, clinicianToken)
      .then(({ data }) => {
        setPrescriptions(data);
        setPrescriptionsOrder(data);
        setIsLoadingPrescriptions(false);
      })
      .catch((error) => {
        console.log("Get patient prescriptions: ", error);
      });

    return () => {
      setPrescriptions([]);
      setIsLoadingPrescriptions(true);
    };
  }, [patientId, software, clinicianToken, rechargeComponent]);

  // Fetching number of sessions by protocol
  useEffect(() => {
    UseFetch("get", `${WEB_SERVICE_MIMS}/sessions/get/${patientId}/${software}`, new AbortController(), null, clinicianToken)
      .then(({ data }) => {
        setProtocolsSessionsNumber(data);
        setIsLoadingSessionsNumber(false);
      })
      .catch((error) => {
        console.log("Get number of sessions by protocol: ", error);
      });
  }, [patientId, software, clinicianToken]);

  // Fetching minutes usage by protocol and coach status
  useEffect(() => {
    const checkMinutesUsage = async () => {
      const isPHRASE = await isPhraseProjectHospital(hospitalName);
      const isNEST = await isNestProjectHospital(hospitalName);

      if (isAppOrPlus(software) && isNEST) setShowCoachStatus(true);

      if (isPHRASE || isNEST) {
        Axios("get", `${WEB_SERVICE_MIMS}/adherence/software/get-minutes-usage/${patientId}/${software}`)
          .then(({ data }) => {
            setMinutesUsage(data);
          })
          .catch((error) => {
            console.log("Get minutes usage by protocol: ", error);
          });
      }
    };
    checkMinutesUsage();

    return () => {
      setMinutesUsage({});
    };
  }, [hospitalName, patientId, software]);

  // Fetching protocol controls
  useEffect(() => {
    if (isPlus(software)) {
      Axios("get", `${WEB_SERVICE_MIMS}/controls/get/${patientId}/${software}`)
        .then(({ data }) => {
          setProtocolControls(data);

          const getProtocolControls = (protocolId) => {
            const filteredProtocolControls = data.filter((control) => control.PROTOCOL_ID === protocolId);
            let protocolControlsToSet = {};

            for (const protocolControl of filteredProtocolControls) {
              protocolControlsToSet[protocolControl.CONTROL_KEY] = {
                CONTROL_VALUE: Number(protocolControl.CONTROL_VALUE),
              };
            }

            return protocolControlsToSet;
          };

          setTwisterBuddyProtocolControls(getProtocolControls(protocols.web.fullBody.TWISTER_BUDDY.id));
          setCostumePartyProtocolControls(getProtocolControls(protocols.web.fullBody.COSTUME_PARTY.id));
          setGentleGiantProtocolControls(getProtocolControls(protocols.web.fullBody.GENTLE_GIANT.id));
        })
        .catch((error) => {
          console.log("Get protocol controls: ", error);
        });
    }

    return () => {
      setProtocolControls({});
      setTwisterBuddyProtocolControls({});
      setCostumePartyProtocolControls({});
      setGentleGiantProtocolControls({});
    };
  }, [patientId, software, rechargeProtocolControls]);

  // Fetching patient devices
  useEffect(() => {
    if (isAppOrPlus(software)) {
      Axios("get", `${WEB_SERVICE_MIMS}/patient-devices/get/${patientId}`)
        .then(({ data }) => {
          const isSomeDeviceIncompatibleWithAR = data.some((device) => device.AR_COMPATIBILITY === 0);
          setIsIncompatibleWithAR(isSomeDeviceIncompatibleWithAR);
        })
        .catch((error) => {
          console.log("Get patient devices: ", error);
        });
    }

    return () => {
      setIsIncompatibleWithAR(false);
    };
  }, [patientId, software]);

  const dismissARWarning = () => {
    Axios("put", `${WEB_SERVICE_MIMS}/patient-devices/dismiss-ar-warning/${patientId}`)
      .then(() => {
        setIsIncompatibleWithAR(false);
      })
      .catch((error) => {
        console.log("Dismiss AR warning: ", error);
      });
  };

  const isLoadingData = () => {
    return isLoadingHospitalProtocols || isLoadingPrescriptions || isLoadingSessionsNumber;
  };

  const renderClinicAndHomeProtocols = () => {
    return (
      <ClinicAndHomeProtocols
        software={software}
        prescriptions={prescriptions}
        prescriptionsOrder={prescriptionsOrder}
        setPrescriptionsOrder={setPrescriptionsOrder}
        prescriptionsToDatabase={prescriptionsToDatabase}
        hospitalProtocols={hospitalProtocols}
        protocolsSessionsNumber={protocolsSessionsNumber}
        isDisabledInputs={isDisabledInputs}
        isColorToggleActive={isColorToggleActive}
      />
    );
  };

  const renderAppProtocols = () => {
    return (
      <AppProtocols
        software={software}
        prescriptions={prescriptions}
        prescriptionsToDatabase={prescriptionsToDatabase}
        hospitalProtocols={hospitalProtocols}
        protocolsSessionsNumber={protocolsSessionsNumber}
        isDisabledInputs={isDisabledInputs}
        minutesUsage={minutesUsage}
        isIncompatibleWithAR={isIncompatibleWithAR}
      />
    );
  };

  const renderPlusProtocols = () => {
    const protocolControlsActions = {
      twisterBuddyProtocolControls,
      setTwisterBuddyProtocolControls,
      twisterBuddyControlsErrors,
      setTwisterBuddyControlsErrors,
      costumePartyProtocolControls,
      setCostumePartyProtocolControls,
      costumePartyControlErrors,
      setCostumePartyControlErrors,
      gentleGiantProtocolControls,
      setGentleGiantProtocolControls,
      gentleGiantControlErrors,
      setGentleGiantControlErrors,
    };

    return (
      <PlusProtocols
        software={software}
        prescriptions={prescriptions}
        prescriptionsToDatabase={prescriptionsToDatabase}
        hospitalProtocols={hospitalProtocols}
        protocolsSessionsNumber={protocolsSessionsNumber}
        isDisabledInputs={isDisabledInputs}
        minutesUsage={minutesUsage}
        platformFilter={platformFilter}
        setPlatformFilter={setPlatformFilter}
        protocolControlsActions={protocolControlsActions}
        rechargeProtocolControls={rechargeProtocolControls}
        setRechargeProtocolControls={setRechargeProtocolControls}
        isIncompatibleWithAR={isIncompatibleWithAR}
      />
    );
  };

  const setProtocolDuration = (protocolId) => {
    const protocolDuration = protocolControls
      .filter((control) => control.PROTOCOL_ID === protocolId && !control.DELETE_TIME)
      .reduce((accumulator, currentValue) => accumulator + Number(currentValue.CONTROL_VALUE), 0);

    for (let prescription of prescriptionsToDatabase) {
      if (prescription.PROTOCOL_ID === protocolId) prescription.SESSION_DURATION = protocolDuration;
    }
  };

  const savePrescriptionsData = async () => {
    // Set Coach status
    if (showCoachStatus) {
      await Axios("post", `${WEB_SERVICE_MIMS}/coach/update-status/${patientId}`, { isCoachActive: isCoachStatus })
        .then(({ data }) => {
          if (data.coachStatusUpdated && !prescriptionsToDatabase.length) {
            toast.success(t("prescriptions_subscriptionAddedMessage"));
            setIsDisabledInputs(true);
            setRechargeComponent(!rechargeComponent);
          }
        })
        .catch((error) => {
          toast.error(t("sendDataErrorMessage"));
          console.log("Update coach status: ", error);
        });
    }
    // Set prescriptions
    if (prescriptionsToDatabase.length) {
      if (isPlus(software)) {
        let isSomeControlError = false;

        const checkPrescriptionWithControls = (protocolId, setError) => {
          const protocolPrescriptions = prescriptionsToDatabase.some(
            (prescription) => prescription.PROTOCOL_ID === protocolId && prescription.STARTING_DATE,
          );

          const protocolLevels = protocolControls.some(
            (control) => control.PROTOCOL_ID === protocolId && control.CONTROL_KEY.includes("level_"),
          );

          if (protocolPrescriptions) {
            if (!protocolLevels) {
              setError(true);
              isSomeControlError = true;
            } else {
              setProtocolDuration(protocolId);
            }
          }
        };

        checkPrescriptionWithControls(protocols.web.fullBody.TWISTER_BUDDY.id, setTwisterBuddyControlsErrors);
        checkPrescriptionWithControls(protocols.web.fullBody.COSTUME_PARTY.id, setCostumePartyControlErrors);
        checkPrescriptionWithControls(protocols.web.fullBody.GENTLE_GIANT.id, setGentleGiantControlErrors);

        if (isSomeControlError) return toast.error(t("controls_addLevelMessage"));
      }

      setIsLoadingPrescriptions(true);

      await Axios("post", `${WEB_SERVICE_MIMS}/prescriptions/add/${patientId}/${software}`, prescriptionsToDatabase)
        .then(({ data }) => {
          if (data.success) {
            toast.success(t("prescriptions_subscriptionAddedMessage"));
            setIsDisabledInputs(true);
            setRechargeComponent(!rechargeComponent);
          } else {
            toast.error(t("sendDataErrorMessage"));
          }
        })
        .catch((error) => {
          toast.error(t("sendDataErrorMessage"));
          console.log("Add prescriptions: ", error);
        });
    } else {
      setIsDisabledInputs(true);
      setIsLoadingPrescriptions(false);
    }
  };

  return (
    <>
      {isLoadingData() ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={software} />
          {isAppOrPlus(software) && (
            <SoftwareMenu
              prescriptionsPath={`${pathHomePage}/prescriptions/${software}/${clinicianToken}/${patientId}`}
              diagnosisPrescriptionsPath={`${pathHomePage}/diagnosis-prescriptions/${clinicianToken}/${patientId}/${software}`}
              questionnairePath={`${pathHomePage}/emotional-questions/${clinicianToken}/${patientId}/${software}`}
              messagesPath={`${pathHomePage}/messages/${clinicianToken}/${patientId}/${software}`}
            />
          )}
          <Title title={`RGS ${software.toUpperCase()}`} subtitle={`/ ${t("prescriptions_title")}`} marginBottom="10px" />
          <PrescriptionsInfo />
          {isIncompatibleWithAR && <DismissibleWarningMessage message={t("ar_incompatibility")} onDismiss={dismissARWarning} />}
          <ActionsManagement
            handleSubmit={savePrescriptionsData}
            isDisabledInputs={isDisabledInputs}
            setIsDisabledInputs={setIsDisabledInputs}
            prescriptionsToDatabase={prescriptionsToDatabase}
            software={software}
            patientId={patientId}
            isColorToggleActive={isColorToggleActive}
            setIsColorToggleActive={setIsColorToggleActive}
          />
          <Form>
            {isClinicOrHome(software) && renderClinicAndHomeProtocols()}
            {isApp(software) && renderAppProtocols()}
            {isPlus(software) && renderPlusProtocols()}
          </Form>
          <RoutineInfo prescriptions={prescriptions} />
          {showCoachStatus && (
            <CoachStatus isCoachStatus={isCoachStatus} setIsCoachStatus={setIsCoachStatus} isDisabledInputs={isDisabledInputs} />
          )}
          <CheckChangesWithoutSaving isDisabledInputs={isDisabledInputs} />
        </>
      )}
    </>
  );
};

export default PatientPrescriptions;
